import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Button, Table, Card, Spinner, Badge, Nav } from "react-bootstrap";
import { barometreOperations, barometreSelector } from "state/ducks/store/barometre";
import Help from "./Help";
import EffectifEditor from "./EffectifEditor";
import ThemeEditor from "./ThemeEditor";

const PRIMARY = "#429853";
const DARK = "#1d1d1b";

const VagueReview = ({ id }) => {
  const dispatch = useDispatch();
  const detail = useSelector(barometreSelector.getDetailSelector);
  const synthese = useSelector(barometreSelector.getSyntheseSelector);
  const history = useSelector(barometreSelector.getHistorySelector);
  const actionState = useSelector(barometreSelector.getActionSelector);

  const [activeCode, setActiveCode] = useState(null);
  const [siteFilter, setSiteFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [valFilter, setValFilter] = useState("all"); // all | todo | done
  const [showEffectif, setShowEffectif] = useState(false);
  const [showThemes, setShowThemes] = useState(false);

  useEffect(() => {
    dispatch(barometreOperations.getDetail(id));
    dispatch(barometreOperations.getSynthese(id));
    dispatch(barometreOperations.getHistory());
  }, [dispatch, id]);

  const barometre = detail.barometre;
  const openQuestions = (detail.questions || []).filter((q) => q.type === "open");

  useEffect(() => {
    if (!activeCode && openQuestions.length > 0) {
      setActiveCode(openQuestions[0].code);
    }
  }, [openQuestions, activeCode]);

  const handleClassify = () => {
    if (
      !window.confirm(
        "Lancer la classification IA ?\n\nElle reclasse toutes les réponses de la vague : " +
          "les thèmes corrigés à la main seront écrasés et les validations remises à zéro."
      )
    ) {
      return;
    }
    // le classify IA est bloquant ; le middleware async renvoie une promesse,
    // on rafraîchit le détail + la synthèse une fois la classification revenue
    Promise.resolve(dispatch(barometreOperations.classify(id))).then(() => {
      dispatch(barometreOperations.getDetail(id));
      dispatch(barometreOperations.getSynthese(id));
    });
  };

  const handleValidate = () => {
    if (window.confirm("Marquer la vague comme validée (relue) ?")) {
      dispatch(barometreOperations.validateVague(id));
    }
  };

  // Remplit les statuts manquants depuis le référentiel employés (ne touche
  // jamais un statut déjà posé — l'historique par vague est figé).
  const handleApplyEmployes = () => {
    if (
      !window.confirm(
        "Remplir les statuts manquants depuis le référentiel employés ?\n\n" +
          "Les statuts déjà renseignés ne seront pas modifiés."
      )
    ) {
      return;
    }
    Promise.resolve(dispatch(barometreOperations.applyEmployes(id))).then(() => {
      dispatch(barometreOperations.getDetail(id));
    });
  };

  const handleExport = () => {
    barometreOperations.downloadExport(id, "btoc_btob", `Barometre-${barometre?.libelle || id}.xlsx`);
  };

  const handleExportPpt = () => {
    barometreOperations.downloadPpt(id, `Barometre-${barometre?.libelle || id}.pptx`);
  };

  const handleVerbatimBlur = (item, value) => {
    if (value !== (item.verbatim || "")) {
      dispatch(barometreOperations.updateItem(item.id, { verbatim: value }));
    }
  };

  const handleThemeChange = (item, value) => {
    dispatch(barometreOperations.updateItem(item.id, { themeId: value ? parseInt(value, 10) : null }));
  };

  const handleValidee = (item, checked) => {
    dispatch(barometreOperations.updateItem(item.id, { validee: checked }));
  };

  const handleStatut = (reponse, value) => {
    if (value !== (reponse.statut || "")) {
      dispatch(barometreOperations.updateReponse(reponse.id, { statut: value || null }));
    }
  };

  const handleSite = (reponse, value) => {
    if (value !== (reponse.site || "")) {
      dispatch(barometreOperations.updateReponse(reponse.id, { site: value || null }));
    }
  };

  const handleSaveEffectif = (effectif) => {
    Promise.resolve(dispatch(barometreOperations.saveEffectif(id, effectif))).then(() => {
      dispatch(barometreOperations.getSynthese(id));
    });
  };

  const handleAddTheme = (question) => {
    const label = window.prompt(`Nouveau thème pour « ${question.code} » :`);
    if (label && label.trim()) {
      dispatch(barometreOperations.createTheme(question.id, label.trim()));
    }
  };

  // Renommage / suppression d'un thème : la synthèse (top thèmes) est
  // recalculée côté serveur, on la rafraîchit une fois l'appel revenu.
  const handleRenameTheme = (question, theme, libelle) => {
    Promise.resolve(dispatch(barometreOperations.updateTheme(theme.id, libelle, question.id))).then(
      () => dispatch(barometreOperations.getSynthese(id))
    );
  };

  const handleDeleteTheme = (question, theme) => {
    Promise.resolve(dispatch(barometreOperations.deleteTheme(theme.id, question.id))).then(() =>
      dispatch(barometreOperations.getSynthese(id))
    );
  };

  if (detail.isLoading && !barometre) {
    return <Spinner animation="border" style={{ marginTop: 24 }} />;
  }
  if (!barometre) {
    return null;
  }

  const activeQuestion = openQuestions.find((q) => q.code === activeCode);

  const options = detail.options || {};

  // sites sélectionnables (présents + « Multi-sites », fournis par l'API)
  const siteOptions =
    options.sites ||
    Array.from(new Set((detail.reponses || []).map((r) => r.site).filter(Boolean))).sort();

  // statuts canoniques (Respo Coord / EP / ES - Adjoint) + ceux déjà présents
  const statutOptions = Array.from(
    new Set([...(options.statutsPersonnel || []), ...(options.statuts || [])])
  );

  // réponses ayant un verbatim non vide pour la question active
  const allRows = (detail.reponses || [])
    .map((r) => ({ reponse: r, item: (r.items || []).find((it) => it.questionId === activeQuestion?.id) }))
    .filter((row) => row.item && (row.item.verbatim || "").trim() !== "");

  const nbValidees = allRows.filter((row) => row.item.themeValidee).length;

  // nb de réponses classées par thème (pour l'éditeur de thèmes : confirmation
  // de suppression informée)
  const themeUsage = {};
  allRows.forEach(({ item }) => {
    if (item.themeId) themeUsage[item.themeId] = (themeUsage[item.themeId] || 0) + 1;
  });

  // application des filtres (site + statut + état de validation)
  const rows = allRows.filter((row) => {
    if (siteFilter === "__none__") {
      if (row.reponse.site) return false;
    } else if (siteFilter && row.reponse.site !== siteFilter) return false;
    if (statutFilter && row.reponse.statut !== statutFilter) return false;
    if (valFilter === "todo" && row.item.themeValidee) return false;
    if (valFilter === "done" && !row.item.themeValidee) return false;
    return true;
  });

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <h4 style={{ color: DARK, margin: 0 }}>
          {barometre.libelle}{" "}
          <Badge variant="secondary" style={{ fontWeight: "normal" }}>
            {barometre.statutFr}
          </Badge>
        </h4>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button variant="outline-primary" size="sm" onClick={handleClassify} disabled={actionState.isClassifying}>
            {actionState.isClassifying ? <Spinner animation="border" size="sm" /> : "Classer avec l'IA"}
          </Button>
          <Help title="Classer avec l'IA" placement="bottom">
            L'IA lit tous les verbatims, dégage les thèmes de cette vague, puis range chaque réponse
            dans <b>un seul thème</b> (son sujet principal). Elle ne réécrit jamais les verbatims. Tu
            peux la relancer : elle repart des verbatims actuels.
          </Help>
          <Button variant="outline-secondary" size="sm" onClick={handleApplyEmployes}>
            Statuts ← référentiel
          </Button>
          <Help title="Statuts depuis le référentiel" placement="bottom">
            Remplit le statut des réponses <b>sans statut</b> en retrouvant chaque personne dans
            la liste « Employés &amp; statuts ». Ne modifie <b>jamais</b> un statut déjà présent :
            chaque vague garde le statut de l'époque (historique d'évolution).
          </Help>
          <Button variant="outline-secondary" size="sm" onClick={handleValidate} disabled={actionState.isValidating}>
            Valider la vague
          </Button>
          <Help title="Valider la vague" placement="bottom">
            Marque la vague comme relue. Tu peux quand même continuer à éditer ensuite. C'est juste
            un repère d'avancement.
          </Help>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowEffectif(true)}>
            Effectif
          </Button>
          <Help title="Effectif" placement="bottom">
            Saisis le nombre de personnes <b>attendues</b> (total + par site + par statut) pour
            calculer le <b>taux de participation</b> affiché dans la synthèse.
          </Help>
          <Button style={{ background: PRIMARY, border: "none" }} size="sm" onClick={handleExport}>
            Export Excel
          </Button>
          <Help title="Export Excel" placement="bottom">
            Génère un fichier Excel : un onglet « Base de données » (mise en page d'origine, colonnes
            Synthèse remplies) + un onglet « Synthèse » (participation, ressenti par site, top
            thèmes).
          </Help>
          <Button variant="outline-dark" size="sm" onClick={handleExportPpt}>
            Convertir en PPT
          </Button>
          <Help title="Convertir en PPT" placement="left">
            Génère une présentation PowerPoint sur le modèle habituel : 1 slide titre, 1 slide par
            site (participation + ressenti), Enthousiasmes, Préoccupations, et « En un mot ».
          </Help>
        </div>
        <EffectifEditor
          show={showEffectif}
          onHide={() => setShowEffectif(false)}
          effectif={barometre.effectif}
          sites={siteOptions}
          statuts={statutOptions}
          onSave={handleSaveEffectif}
        />
        <ThemeEditor
          show={showThemes}
          onHide={() => setShowThemes(false)}
          question={activeQuestion}
          usage={themeUsage}
          onRename={(theme, libelle) => handleRenameTheme(activeQuestion, theme, libelle)}
          onDelete={(theme) => handleDeleteTheme(activeQuestion, theme)}
          onCreate={(libelle) => dispatch(barometreOperations.createTheme(activeQuestion.id, libelle))}
        />
      </div>

      <SynthesePanel synthese={synthese} history={history} activeCode={activeCode} />

      <div style={{ display: "flex", alignItems: "center", marginTop: 16 }}>
        <small style={{ color: "#888", marginRight: 4 }}>Axes</small>
        <Help title="Les axes" placement="right">
          Un onglet par question ouverte du baromètre (enthousiasme, préoccupation, et les questions
          du rush en BtoB). Chaque axe a ses propres thèmes et se relit séparément.
        </Help>
      </div>
      <Nav variant="tabs" activeKey={activeCode} onSelect={(k) => setActiveCode(k)} style={{ marginTop: 4 }}>
        {openQuestions.map((q) => (
          <Nav.Item key={q.code}>
            <Nav.Link eventKey={q.code}>{q.code}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {activeQuestion && (
        <Card style={{ borderTop: "none" }}>
          <Card.Body>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: "#666", fontStyle: "italic" }}>{activeQuestion.libelle}</div>
              <div style={{ whiteSpace: "nowrap" }}>
                <small style={{ color: "#888", marginRight: 10 }}>
                  {nbValidees}/{allRows.length} validés
                </small>
                <Button variant="link" size="sm" onClick={() => handleAddTheme(activeQuestion)}>
                  + thème
                </Button>
                <Button variant="link" size="sm" onClick={() => setShowThemes(true)}>
                  ✎ thèmes
                </Button>
                <Help title="Gérer les thèmes" placement="left">
                  « + thème » crée un thème pour cet axe. « ✎ thèmes » ouvre la liste pour{" "}
                  <b>renommer</b> ou <b>supprimer</b> un thème (les réponses classées dessus
                  repassent à « aucun »). Les thèmes apparaissent dans le menu déroulant de la
                  colonne « Thème ».
                </Help>
              </div>
            </div>

            {/* Barre de filtres */}
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Form.Label style={{ margin: 0, fontSize: 13 }}>Site</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                  style={{ width: "auto" }}
                >
                  <option value="">Tous</option>
                  {siteOptions.map((s) => (
                    <option key={s} value={s}>
                      {s.replace("Le Moulin - ", "")}
                    </option>
                  ))}
                  <option value="__none__">(Sans site)</option>
                </Form.Control>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Form.Label style={{ margin: 0, fontSize: 13 }}>Statut</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={statutFilter}
                  onChange={(e) => setStatutFilter(e.target.value)}
                  style={{ width: "auto" }}
                >
                  <option value="">Tous</option>
                  {statutOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Control>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Form.Label style={{ margin: 0, fontSize: 13 }}>État</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={valFilter}
                  onChange={(e) => setValFilter(e.target.value)}
                  style={{ width: "auto" }}
                >
                  <option value="all">Tous</option>
                  <option value="todo">À valider</option>
                  <option value="done">Validés</option>
                </Form.Control>
                <Help title="Filtrer" placement="right">
                  Affiche seulement les réponses d'un <b>site</b>, ou seulement celles qui restent{" "}
                  <b>à valider</b> (pratique pour finir la relecture). Les filtres n'agissent que sur
                  l'affichage, pas sur la synthèse.
                </Help>
              </div>
              <small style={{ color: "#aaa" }}>
                {rows.length} affichée{rows.length > 1 ? "s" : ""}
              </small>
            </div>

            <Table responsive size="sm" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th style={{ width: "18%" }}>
                    Personne
                    <Help title="Personne & statut">
                      Le nom et le site viennent du fichier. Le <b>statut RH</b> (Employé / CEA /
                      Cadre) est saisi par toi — repris de ta colonne à l'import, et corrigeable ici
                      d'un clic. L'IA ne le devine jamais.
                    </Help>
                  </th>
                  <th style={{ width: "42%" }}>
                    Verbatim
                    <Help title="Verbatim">
                      Ce que la personne a écrit, tel quel. Tu peux le corriger ici (faute de frappe,
                      etc.) — il n'est jamais modifié par l'IA.
                    </Help>
                  </th>
                  <th style={{ width: "26%" }}>
                    Thème (Synthèse)
                    <Help title="Thème = sujet principal">
                      Le sujet principal de la réponse, pré-rempli par l'IA et modifiable. « proposé
                      IA » = suggestion automatique ; bord vert + « validé main » = choisi/confirmé
                      par toi. « — aucun — » retire le thème.
                    </Help>
                  </th>
                  <th style={{ width: "14%" }}>
                    Validé
                    <Help title="Validé" placement="left">
                      Coche quand le thème te convient. Le compteur « X/Y validés » suit ton
                      avancement, et le filtre « À valider » t'amène droit au reste.
                    </Help>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ reponse, item }) => (
                  <tr key={item.id} style={{ background: item.themeValidee ? "#f3faf5" : "transparent" }}>
                    <td>
                      <div>{reponse.personne}</div>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={reponse.statut || ""}
                        onChange={(e) => handleStatut(reponse, e.target.value)}
                        style={{ marginTop: 2, marginBottom: 2, fontSize: 12, padding: "0 4px", height: 26 }}
                      >
                        <option value="">— statut —</option>
                        {statutOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={reponse.site || ""}
                        onChange={(e) => handleSite(reponse, e.target.value)}
                        style={{ fontSize: 12, padding: "0 4px", height: 26, color: "#777" }}
                      >
                        <option value="">— site —</option>
                        {siteOptions.map((s) => (
                          <option key={s} value={s}>
                            {s.replace("Le Moulin - ", "")}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        defaultValue={item.verbatim || ""}
                        key={item.id + "-" + (item.verbatim || "").length}
                        onBlur={(e) => handleVerbatimBlur(item, e.target.value)}
                        style={{ fontSize: 13 }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={item.themeId || ""}
                        onChange={(e) => handleThemeChange(item, e.target.value)}
                        style={{
                          borderColor: item.themeSource === "humain" ? PRIMARY : undefined,
                        }}
                      >
                        <option value="">— aucun —</option>
                        {(activeQuestion.themes || []).map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.libelle}
                          </option>
                        ))}
                      </Form.Control>
                      {item.themeSource && (
                        <small style={{ color: item.themeSource === "humain" ? PRIMARY : "#999" }}>
                          {item.themeSource === "ia" ? "proposé IA" : "validé main"}
                        </small>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Form.Check
                        type="checkbox"
                        checked={!!item.themeValidee}
                        onChange={(e) => handleValidee(item, e.target.checked)}
                      />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#888" }}>
                      Aucun verbatim. Lance « Classer avec l'IA » après l'import.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

/** Panneau synthèse : ressenti par site + top thèmes de l'axe + historique. */
const pctTxt = (x) => (x == null ? "—" : Math.round(x * 100) + "%");
const colSmall = { fontSize: 13, color: "#555" };

const SynthesePanel = ({ synthese, history, activeCode }) => {
  if (synthese.isLoading || !synthese.data) {
    return null;
  }
  const data = synthese.data;
  const sites = Object.keys(data.parSite || {});
  const statuts = Object.keys(data.parStatut || {});
  const g = data.global || {};
  const gr = g.ressenti || {};
  const axe = g.axes ? g.axes[activeCode] : null;

  const partTxt = (d) =>
    d && d.taux != null ? `${d.participation}/${d.effectif} (${pctTxt(d.taux)})` : (d ? `${d.participation}` : "—");

  return (
    <Card style={{ marginTop: 12, background: "#fafafa" }}>
      <Card.Body style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
        {/* Participation */}
        <div>
          <strong style={{ color: DARK }}>Participation</strong>
          <Help title="Participation" placement="top">
            Réponses reçues / effectif attendu. Renseigne l'effectif (bouton « Effectif ») pour
            afficher le taux, global et par site.
          </Help>
          <div>
            Global : <b>{partTxt(g)}</b>
          </div>
          {sites.map((s) => (
            <div key={s} style={colSmall}>
              {s.replace("Le Moulin - ", "")} : <b>{partTxt(data.parSite[s])}</b>
            </div>
          ))}
          {(data.sansSite || 0) > 0 && (
            <div style={{ ...colSmall, color: "#888" }} title="Réponses sans site renseigné (ex. onglet BtoB du formulaire) — à requalifier via le filtre « (Sans site) » de la liste">
              Sans site : <b>{data.sansSite}</b>
            </div>
          )}
        </div>

        {/* Ressenti */}
        <div>
          <strong style={{ color: DARK }}>Ressenti</strong>
          <Help title="Ressenti" placement="top">
            « Bien » = Plutôt bien + Super bien (niveaux 3-4). Moyenne sur 4 ; le chiffre entre
            parenthèses est l'évolution vs la vague précédente.
          </Help>
          <div>
            <b style={{ color: PRIMARY }}>{pctTxt(gr.pctBien)}</b> se sentent bien ·{" "}
            <span style={{ color: "#c0392b" }}>{pctTxt(gr.pctPasBien)}</span> pas bien
          </div>
          <div>
            Moyenne : <b>{gr.moyenne != null ? gr.moyenne : "—"}</b>/4
            {gr.evolutionMoyenne != null && (
              <span style={{ color: gr.evolutionMoyenne >= 0 ? PRIMARY : "#c0392b" }}>
                {" "}
                ({gr.evolutionMoyenne >= 0 ? "+" : ""}
                {gr.evolutionMoyenne})
              </span>
            )}
          </div>
          {sites.map((s) => (
            <div key={s} style={colSmall}>
              {s.replace("Le Moulin - ", "")} : <b>{pctTxt(data.parSite[s].ressenti.pctBien)}</b> bien ·{" "}
              <span style={{ color: "#c0392b" }}>{pctTxt(data.parSite[s].ressenti.pctPasBien)}</span> pas bien
              {" "}(moy {data.parSite[s].ressenti.moyenne != null ? data.parSite[s].ressenti.moyenne : "—"})
            </div>
          ))}
        </div>

        {/* Par statut */}
        {statuts.length > 0 && (
          <div>
            <strong style={{ color: DARK }}>Par statut</strong>
            <Help title="Par statut" placement="top">
              Participation et ressenti par statut du personnel (Respo Coord / Employé Polyvalent /
              ES - Adjoint).
            </Help>
            {statuts.map((st) => (
              <div key={st} style={colSmall}>
                {st} : <b>{partTxt(data.parStatut[st])}</b> ·{" "}
                {pctTxt(data.parStatut[st].ressenti.pctBien)} bien
              </div>
            ))}
          </div>
        )}

        {axe && (
          <div>
            <strong style={{ color: DARK }}>Top thèmes — {activeCode}</strong>
            <Help title="Top thèmes" placement="top">
              Les thèmes les plus fréquents de l'axe affiché, en % des réponses classées (et le
              nombre de réponses). Se met à jour quand tu corriges des thèmes.
            </Help>
            {(axe.themes || []).slice(0, 5).map((t) => (
              <div key={t.theme} style={{ fontSize: 13, color: "#555" }}>
                {Math.round(t.pct * 100)}% — {t.theme} <span style={{ color: "#aaa" }}>({t.n})</span>
              </div>
            ))}
          </div>
        )}

        {history.data && history.data.length > 0 && (
          <div>
            <strong style={{ color: DARK }}>Historique ressenti</strong>
            <Help title="Historique" placement="top">
              Évolution de la note moyenne « je me sens » d'une vague à l'autre. Permet de voir si le
              ressenti progresse dans le temps, globalement et par site.
            </Help>
            {history.data.map((h) => (
              <div key={h.date} style={{ fontSize: 13, color: "#555" }}>
                {h.libelle} : <b>{h.moyenne}</b>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default VagueReview;
